import { SEARCH_KEYS } from '../constant/SearchKeys';
import { PAYMENTS_SEARCH_PLACEHOLDERS } from '../constant/paymentsSearchPlaceholders';
import {
  ROYALTIES_ANZ_HISTORICAL_BANNER_KEYS,
  ROYALTIES_WORLD_HISTORICAL_BANNER_KEYS,
  type RoyaltiesHistoricalBannerConfigKeys,
} from '../constant/royaltiesHistoricalBanner';
import type {
  RoyaltiesSectionTab,
  RoyaltiesSectionTabsLayoutProps,
} from './RoyaltiesSectionTabsLayout';

export const ROYALTIES_HISTORIC_TABS = [
  {
    agentOnly: false,
    label: 'Past Statements',
    section: 'statements',
  },
  {
    agentOnly: true,
    label: 'Past Consolidated Statements',
    section: 'statement-summaries',
  },
  {
    agentOnly: false,
    label: 'Paid Royalties',
    // MSR2's royalty search (by title / client, plus advanced filters).
    section: 'paid-royalties',
  },
] as const satisfies readonly RoyaltiesSectionTab[];

/** Title + Earnings tabs shared by Royalties ANZ and World. */
export const ROYALTIES_TITLE_EARNINGS_TABS = [
  {
    agentOnly: false,
    label: 'Title Statements',
    section: 'title-statements',
  },
  {
    agentOnly: false,
    // Not agent-gated: a direct member's earnings are invoiced to their own
    // workspace, so this table has rows for them too (APP-2370).
    label: 'Earnings Statements',
    section: 'earnings-statements',
  },
] as const satisfies readonly RoyaltiesSectionTab[];

/** Shared shell props for {@link RoyaltiesLayout} (Historic Royalties / MSR2 embed). */
export const royaltiesHistoricLayoutConfig = {
  basePathSegment: 'royalties',
  defaultSection: 'statements',
  tabs: ROYALTIES_HISTORIC_TABS,
} as const;

/**
 * Props for the shared region shell. `tabs` comes from the `Pick`, so a future
 * region is free to bring a different tab set.
 */
export type RoyaltiesMsr3RegionLayoutConfig = Pick<
  RoyaltiesSectionTabsLayoutProps,
  | 'basePathSegment'
  | 'defaultSection'
  | 'outletWrapperClassName'
  | 'pageTitle'
  | 'tabs'
> & {
  /** Parameter Store keys for this region's historical cut-off banner. */
  historicalBannerConfigKeys: RoyaltiesHistoricalBannerConfigKeys;
};

const royaltiesTitleEarningsLayoutConfig = {
  defaultSection: 'title-statements',
  outletWrapperClassName: 'flex w-full flex-col gap-4',
  tabs: ROYALTIES_TITLE_EARNINGS_TABS,
} as const;

/** Shared shell props for Royalties ANZ (Title + Earnings tables). */
export const royaltiesAnzLayoutConfig = {
  ...royaltiesTitleEarningsLayoutConfig,
  basePathSegment: 'royalties-anz',
  historicalBannerConfigKeys: ROYALTIES_ANZ_HISTORICAL_BANNER_KEYS,
  pageTitle: 'Royalties ANZ',
} as const satisfies RoyaltiesMsr3RegionLayoutConfig;

/**
 * Shared shell props for Royalties World. Same tabs as ANZ; route
 * segment, banner keys and table keys differ. Table chrome lives in RoyaltiesListTable.
 */
export const royaltiesWorldLayoutConfig = {
  ...royaltiesTitleEarningsLayoutConfig,
  basePathSegment: 'royalties-world',
  historicalBannerConfigKeys: ROYALTIES_WORLD_HISTORICAL_BANNER_KEYS,
  pageTitle: 'Royalties World',
} as const satisfies RoyaltiesMsr3RegionLayoutConfig;

export type RoyaltiesSection =
  (typeof ROYALTIES_TITLE_EARNINGS_TABS)[number]['section'];

/** Region-invariant table chrome: sort fields and which columns exist. */
const ROYALTIES_TABLE_SHAPE = {
  'earnings-statements': {
    dateSortProperty: 'invoiceDate',
    defaultSortBy: 'invoiceDate:desc',
    referenceSortProperty: 'invoiceNo',
    showPayoutReferenceColumn: true,
    showTitleColumn: false,
    tableKey: 'earnings',
  },
  'title-statements': {
    dateSortProperty: 'statementDate',
    defaultSortBy: 'statementDate:desc',
    referenceSortProperty: 'statementNo',
    showPayoutReferenceColumn: false,
    showTitleColumn: true,
    tableKey: 'title',
  },
} as const;

/**
 * Per-section chrome for Royalties ANZ list tables. URL keys / table id are
 * derived from `tableKey` via {@link royaltiesTableKeys}. Query/map/download
 * live in RoyaltiesListTable.
 */
const ROYALTIES_ANZ_TABLES = {
  'earnings-statements': {
    ...ROYALTIES_TABLE_SHAPE['earnings-statements'],
    searchKey: SEARCH_KEYS.ROYALTIES_ANZ_EARNINGS_SEARCH,
    // Same copy for every workspace type: reference ID is the only searchable
    // field on an earnings roll-up, so there is nothing for `isAgent` to vary.
    searchPlaceholder: PAYMENTS_SEARCH_PLACEHOLDERS.earningsReference,
  },
  'title-statements': {
    ...ROYALTIES_TABLE_SHAPE['title-statements'],
    agentSearchPlaceholder: PAYMENTS_SEARCH_PLACEHOLDERS.anzTitleAgent,
    searchKey: SEARCH_KEYS.ROYALTIES_ANZ_TITLE_SEARCH,
    searchPlaceholder: PAYMENTS_SEARCH_PLACEHOLDERS.anzTitleMember,
  },
} as const;

/**
 * Per-section chrome for Royalties World list tables.
 * Deliberately its own search keys and placeholders: reusing
 * {@link ROYALTIES_ANZ_TABLES} would write `?royaltiesAnzTitleSearch=` from
 * the World page, so navigating between regions would carry the search
 * term across and both pages would fight over one param.
 */
const ROYALTIES_WORLD_TABLES = {
  'earnings-statements': {
    ...ROYALTIES_TABLE_SHAPE['earnings-statements'],
    searchKey: SEARCH_KEYS.ROYALTIES_WORLD_EARNINGS_SEARCH,
    // Shared with ANZ deliberately — unlike the title placeholders, this copy
    // names no region-specific concept, so there is nothing to reword per region.
    searchPlaceholder: PAYMENTS_SEARCH_PLACEHOLDERS.earningsReference,
  },
  'title-statements': {
    ...ROYALTIES_TABLE_SHAPE['title-statements'],
    agentSearchPlaceholder: PAYMENTS_SEARCH_PLACEHOLDERS.worldTitleAgent,
    searchKey: SEARCH_KEYS.ROYALTIES_WORLD_TITLE_SEARCH,
    searchPlaceholder: PAYMENTS_SEARCH_PLACEHOLDERS.worldTitleMember,
  },
} as const;

/** Look up either region's table config by a `product` discriminator. */
export const ROYALTIES_LIST_TABLES = {
  anz: ROYALTIES_ANZ_TABLES,
  world: ROYALTIES_WORLD_TABLES,
} as const;

export type RoyaltiesTableKey =
  (typeof ROYALTIES_TABLE_SHAPE)[RoyaltiesSection]['tableKey'];

export type RoyaltiesRegion = 'anz' | 'world';

/**
 * Per-region prefixes for derived URL params and DOM ids. Paired in one lookup
 * so a caller cannot mix an ANZ search-param prefix with a World
 * table id.
 */
const ROYALTIES_REGION_KEY_PREFIXES = {
  anz: { searchParam: 'royaltiesAnz', tableId: 'royalties-anz' },
  world: { searchParam: 'royaltiesWorld', tableId: 'royalties-world' },
} as const satisfies Record<
  RoyaltiesRegion,
  { searchParam: string; tableId: string }
>;

/**
 * URL / DOM keys for one region's list table. `searchParam` drives both the
 * pagination and sort params, so the two regions cannot collide on either.
 * Keep call sites on this helper so the query param names stay stable.
 */
export const royaltiesTableKeys = (
  region: RoyaltiesRegion,
  tableKey: RoyaltiesTableKey,
) => {
  const { searchParam, tableId } = ROYALTIES_REGION_KEY_PREFIXES[region];
  const sectionPrefix =
    tableKey === 'title' ? `${searchParam}Title` : `${searchParam}Earnings`;

  return {
    paginationKey: `${sectionPrefix}StatementsPage`,
    sortByKey: `${sectionPrefix}Sort`,
    tableId: `${tableId}-${tableKey}-statements-table`,
  } as const;
};
